import { z } from "zod";
import { answerSchema } from "./answers";
import { publicSlideSchema } from "./deck";
import { qnaQuestionSchema } from "./qna";

const uuid = z.string().uuid();

/**
 * What the audience can send the big screen with one tap. Names, not emoji, on the wire:
 * each screen draws its own, and nothing else can be sent.
 */
export const REACTIONS = ["like", "love", "laugh", "clap", "party", "wow"] as const;
export const reactionSchema = z.enum(REACTIONS);
export type Reaction = z.infer<typeof reactionSchema>;
export const REACTION_EMOJI: Record<Reaction, string> = {
  like: "👍",
  love: "❤️",
  laugh: "😂",
  clap: "👏",
  party: "🎉",
  wow: "😮",
};

/**
 * Client → server, over the Centrifugo RPC proxy to POST /realtime/rpc.
 *
 * Every payload is `.strict()`, which is doing real work for Rule 2: a client that
 * sends its own `responseMs`, `submittedAt` or `points` is REJECTED rather than having
 * the field quietly ignored. Silently dropping it would leave a client author
 * believing their timing was accepted, and would leave the next reader of this schema
 * unsure whether it was. The server measures `received_at - opened_at`; there is no
 * client-supplied timing anywhere in this union, by construction.
 */
export const rpcSchema = z.discriminatedUnion("method", [
  z
    .object({
      method: z.literal("answer.submit"),
      params: z.object({ roundId: uuid, answer: answerSchema }).strict(),
    })
    .strict(),
  z
    .object({
      method: z.literal("qna.ask"),
      params: z
        .object({
          body: z.string().trim().min(1).max(500),
          isAnonymous: z.boolean().default(false),
        })
        .strict(),
    })
    .strict(),
  z
    .object({
      method: z.literal("qna.upvote"),
      params: z.object({ questionId: uuid }).strict(),
    })
    .strict(),
  // A participant catching up after (re)connecting — see participantStateSchema.
  z.object({ method: z.literal("session.state"), params: z.object({}).strict() }).strict(),
  z.object({ method: z.literal("qna.list"), params: z.object({}).strict() }).strict(),
  z.object({ method: z.literal("reaction.send"), params: z.object({ reaction: reactionSchema }).strict() }).strict(),
]);

export type Rpc = z.infer<typeof rpcSchema>;
export type RpcMethod = Rpc["method"];

/** One row of the leaderboard, as the presenter sees it. */
export const leaderboardEntrySchema = z.object({
  participantId: uuid,
  displayName: z.string().nullable(),
  score: z.number().int(),
  rank: z.number().int().positive(),
});

export type LeaderboardEntry = z.infer<typeof leaderboardEntrySchema>;

/**
 * Someone who has joined, as the lobby of a quiz shows them: their name on the screen,
 * so each person sees they are in. Only named participants — an anonymous session, or
 * someone who skipped the name, is in the count but never on this list.
 */
export const joinedNameSchema = z.object({
  participantId: uuid,
  displayName: z.string(),
});

export type JoinedName = z.infer<typeof joinedNameSchema>;

/** How many of the latest names travel with a headcount, and so can be on the lobby at once. */
export const JOINED_SHOWN = 50;

/**
 * Where the room is in the deck: which slide is up, whether the leaderboard is showing
 * after it, or — once a quiz's last slide is done — the hall of fame. The presenter's single "Next" walks this forward, and every screen
 * showing the session — the console, the projector — renders from it.
 *
 * `step` counts every move. "Next" names the step it was pressed on, so a double click,
 * or the same key pressed on two screens, moves the room once rather than twice.
 */
export const STAGE_PHASES = ["slide", "leaderboard", "podium"] as const;

export const stageSchema = z
  .object({
    step: z.number().int().nonnegative(),
    slideId: uuid.nullable(),
    phase: z.enum(STAGE_PHASES),
  })
  .strict();

export type Stage = z.infer<typeof stageSchema>;

/**
 * Server → client, published to the three channels.
 *
 * One union across all channels rather than three: the participant PWA has a single
 * message handler, and an exhaustive switch over one union is what makes an unhandled
 * event a compile error there.
 */
export const serverEventSchema = z.discriminatedUnion("type", [
  // session:{id}
  z.object({
    type: z.literal("slide.changed"),
    slideIndex: z.number().int().nonnegative(),
    slideId: uuid,
    // The slide itself, as participants may see it. Without it a phone knows a slide
    // changed but not what to show — there is no other way for it to learn the prompt.
    slide: publicSlideSchema,
  }),
  z.object({
    type: z.literal("round.opened"),
    roundId: uuid,
    slideId: uuid,
    // Server time, and the window length. The client renders a countdown from these;
    // it never reports back how long it thought the answer took.
    openedAt: z.string().datetime(),
    timeLimitMs: z.number().int().nonnegative(),
  }),
  z.object({ type: z.literal("round.closed"), roundId: uuid }),
  z.object({
    type: z.literal("results.revealed"),
    roundId: uuid,
    tallies: z.record(z.string(), z.number().int().nonnegative()),
    correctOptionIds: z.array(uuid).optional(),
    /** A scored type_answer slide: what counted as right, now the round is over. */
    acceptedAnswers: z.array(z.string()).optional(),
  }),
  z.object({ type: z.literal("session.ended") }),

  // session:{id}:host
  z.object({
    type: z.literal("tally.updated"),
    roundId: uuid,
    counts: z.record(z.string(), z.number().int().nonnegative()),
    responseCount: z.number().int().nonnegative(),
    // Free-text slides only: the most recent answers, newest first. Text is not
    // tallied (see tallyFieldsFor), so without this the presenter would see a count
    // and nothing else.
    texts: z.array(z.string()).optional(),
  }),
  z.object({
    // The lobby's QR code blown up to fill the screen, or back to its place: one
    // presenter's choice, followed by every screen of theirs (host channel only).
    type: z.literal("lobby.qr"),
    large: z.boolean(),
  }),
  z.object({
    type: z.literal("participants.updated"),
    count: z.number().int().nonnegative(),
    // The latest JOINED_SHOWN names, newest first, in a session that keeps names. The
    // whole list rather than the one who just joined: this event is throttled and only
    // the latest of a burst is delivered (Rule 4), so each must stand on its own.
    recent: z.array(joinedNameSchema).max(JOINED_SHOWN).optional(),
  }),
  // The audience's reactions since the last batch, added up (ReactionBatcher): at most
  // one batch per session every few hundred milliseconds, however hard the room taps.
  z.object({
    type: z.literal("reactions"),
    counts: z.record(reactionSchema, z.number().int().positive()),
  }),
  // To the host channel for the screens, and to the room so phones know when the hall
  // of fame is up and can show each person their own final place.
  z.object({ type: z.literal("stage.changed"), stage: stageSchema }),
  z.object({
    type: z.literal("leaderboard.updated"),
    entries: z.array(leaderboardEntrySchema),
  }),
  // A scored round's highlights, at close: the fastest correct answer and how many
  // got it right. For the leaderboard that follows it.
  z.object({
    type: z.literal("round.stats"),
    roundId: uuid,
    fastest: z
      .object({ participantId: uuid, displayName: z.string().nullable(), responseMs: z.number().int().nonnegative() })
      .nullable(),
    correctCount: z.number().int().nonnegative(),
    responseCount: z.number().int().nonnegative(),
  }),
  // A question the recipient has not seen before: every new one to the host channel,
  // and to the room once it is visible.
  z.object({ type: z.literal("qna.question"), question: qnaQuestionSchema }),
  z.object({
    type: z.literal("qna.updated"),
    questionId: uuid,
    status: z.enum(["pending", "approved", "answered", "rejected"]),
    upvoteCount: z.number().int().nonnegative(),
  }),

  // session:{id}:p:{participantId}
  // The presenter removed this person: the phone says so and stops.
  z.object({ type: z.literal("participant.removed") }),
  z.object({
    type: z.literal("score.updated"),
    score: z.number().int(),
    rank: z.number().int().positive().nullable(),
    lastAwarded: z.number().int().nonnegative(),
  }),
]);

export type ServerEvent = z.infer<typeof serverEventSchema>;
export type ServerEventType = ServerEvent["type"];

/**
 * Centrifugo connect proxy → POST /realtime/connect.
 *
 * Two kinds of client connect to the same broker, and they prove themselves in
 * completely different ways.
 *
 * A PARTICIPANT presents the join code plus their rejoin token — a bearer credential
 * hashed at rest (`participants.rejoin_token_hash`) and never logged.
 *
 * A PRESENTER presents a short-lived host token, minted by an authenticated HTTP call
 * and scoped to one session. Their organisation session cookie deliberately does NOT
 * travel this path: it would put a long-lived, org-wide credential into a payload
 * forwarded through a second service, to authorise one channel subscription. The host
 * token can do exactly one thing and expires in minutes.
 *
 * A plain union rather than a discriminated one — there is no shared tag to
 * discriminate on, and adding a synthetic `role` field would let a client ASSERT its
 * role, which is precisely the decision the server must keep.
 */
export const participantConnectSchema = z
  .object({
    joinCode: z.string().trim().min(4).max(12),
    rejoinToken: z.string().min(16),
  })
  .strict();

export const hostConnectSchema = z.object({ hostToken: z.string().min(16) }).strict();

export const connectRequestSchema = z.union([participantConnectSchema, hostConnectSchema]);

export type ConnectRequest = z.infer<typeof connectRequestSchema>;

/** Narrow a parsed connect request without re-validating it. */
export function isHostConnect(
  req: ConnectRequest,
): req is z.infer<typeof hostConnectSchema> {
  return "hostToken" in req;
}

/**
 * Reply to the participant's `session.state` RPC: everything needed to render the
 * right screen after joining or reconnecting — a phone that slept through a round
 * opening must wake up on that round, not on a blank waiting screen.
 *
 * Served from Valkey alone. A room reconnects all at once after a Wi-Fi blip, and
 * that is not a moment to send every phone to Postgres.
 */
export const participantStateSchema = z
  .object({
    /** Null before the presenter has put a slide on screen (the lobby). */
    slide: publicSlideSchema.nullable(),
    round: z
      .object({
        roundId: uuid,
        slideId: uuid,
        openedAt: z.string().datetime(),
        timeLimitMs: z.number().int().nonnegative(),
        closed: z.boolean(),
        answered: z.boolean(),
        /** Present once the presenter has revealed the results. */
        results: z
          .object({
            tallies: z.record(z.string(), z.number().int().nonnegative()),
            correctOptionIds: z.array(uuid).optional(),
            acceptedAnswers: z.array(z.string()).optional(),
          })
          .nullable(),
      })
      .nullable(),
    score: z.number().int(),
    rank: z.number().int().positive().nullable(),
    /** The hall of fame is on screen: the quiz is over, bar the presenter ending it. */
    podium: z.boolean(),
    /** For correcting the phone's clock in the countdown; the server enforces the deadline. */
    serverTime: z.string().datetime(),
  })
  .strict();

export type ParticipantState = z.infer<typeof participantStateSchema>;
